<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit"
                 class="p-mr-3">
              {{ $t('Invoices') }}
            </div>

            <Button label="Generate All"
                    icon="ti-plus"
                    class="p-button-outlined p-button-secondary"
                    :disabled="dataTableIsLoading || generateAllIsLoading"
                    @click="openGenerateAllConfirm"/>
          </template>
          <template #end>
            <div class="p-d-flex p-ai-center p-mr-2">
              <Checkbox :disabled="dataTableIsLoading"
                        @change="getItems(true)"
                        id="billing-invoices-show-only-unpaid-inputswitch"
                        :binary="true"
                        v-model="showOnlyUnpaid"/>
              <div>
                <label for="billing-invoices-show-only-unpaid-inputswitch">
                  <div class="pointer p-p-1">{{ $t('showOnlyUnpaidLabel') }}</div>
                </label>
              </div>
            </div>
            <DatesRangeCalendar :disabled="dataTableIsLoading"
                                ref="datesRangeCalendar"
                                :isEmptyByDefault="true"
                                @change-dates-range="changeDatesRange"
                                class="p-mr-2"/>
            <SearchInput :disabled="dataTableIsLoading"
                         @change-processed-search-value="changeProcessedSearchValue"
                         v-model="searchData"
                         @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
              {name: 'number', title: $t('Number'), minWidth: '150px', sortable: true},
              {name: 'export_number', icon: 'ti-link', sortable: false},
              {name: 'tenant', title: $t('Tenant'), sortable: false},
              {name: 'company', title: $t('Company'), sortable: false},
              {name: 'email', title: $t('Billing Email'), sortable: false},
              {name: 'invoice_date', title: $t('issuedLabel'), sortable: true},
              {name: 'payment_due', title: $t('paymentDueLabel'), sortable: false},
              {name: 'overdue', title: '', sortable: false},
              {name: 'period', title: $t('Period'), sortable: false},
              {name: 'total', title: $t('Sum'), sortable: false},
              {name: 'unpaid', title: $t('Due'), sortable: false},
              // {name: 'unpaid', title: $t('Unpaid'), sortable: false},
              {name: 'state', title: $t('Status'), sortable: false},
              {name: '', title: null, sortable: false},
              {name: 'message_icon', title: null, sortable: false},
              {name: 'sent_at', title: null, sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="item.id ?? index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span>{{ item.number }}</span>
              </td>
              <td>
                <div v-if="item.export_number || item.export_id" >
                  <i class="ti-link" v-tooltip.top="item.export_number"/>
                  <ChangedAfterExportIcon :show="item.changed_after_export" size="1.05rem" top="-1px"/>
                </div>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.tenant_name">{{ item.tenant_name }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.company_name">{{ item.company_name }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.billing_email">{{ item.billing_email }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.invoice_date">{{ formatUtcDay(item.invoice_date) }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.due_at"
                      :class="{'warning-color': item.due_at && (countOverdueDays(item.due_at) <= 0) && item.state && +item.unpaid_amount > 0 && [2, 4, 7].includes(+item.state) }">
                  {{ formatUtcDay(item.due_at) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.due_at && item.state && +item.unpaid_amount > 0 && [2, 4, 7].includes(+item.state)"
                      :class="{'warning-color': countOverdueDays(item.due_at) <= 0 }">
                  {{ countOverdueDays(item.due_at) }}
                </span>
              </td>

              <td>
                <span v-if="item.period_start && item.period_end">
                  {{ formatUtcDay(item.period_start) }} – {{ formatUtcDay(item.period_end - 1) }}
                </span>
                <span v-else-if="item.period_start">
                  {{ formatUtcDay(item.period_start) }} –
                </span>
                <span v-else-if="item.period_end">
                  – {{ formatUtcDay(item.period_end - 1) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span>{{ formatCurrency(item.total) }}</span>
              </td>

              <td>
               <span
                   :class="{
                      'warning-color':
                        item.due_at
                        && countOverdueDays(item.due_at) <= 0
                        && +item.unpaid_amount > 0
                        && [2, 4, 7].includes(+item.state)
                    }">
                  {{ formatCurrency(item.unpaid_amount) }}
                </span>
              </td>

              <td>
                <div class="p-d-flex p-flex-wrap p-ai-center" v-if="item.state && +item.state">
                  <div class="state-badge"
                       :class="BillingInvoiceStates.find(state => state.num === +item.state)?.color || ''">
                    {{ BillingInvoiceStates.find(state => state.num === +item.state)?.label[$i18n.locale] || '' }}
                  </div>
                </div>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <DownloadPdfButton :item="item" isSmall type="billing"/>
              </td>

              <td>
                <!--                <EmailSendButton :item="item"/>-->
                <!--                <Button :disabled="dataTableIsLoading"-->
                <!--                        v-tooltip.top="$t('Send')"-->
                <!--                        class="p-button-outlined p-button-secondary p-button-sm p-d-flex p-ai-center p-jc-center"-->
                <!--                        style="width: 36px;"-->
                <!--                        @click="handleSendInvoice(item)">-->
                <!--                  <i class="ti-email"/>-->
                <!--                </Button>-->
                <Button :disabled="!canSendInvoice(item) || dataTableIsLoading || invoiceActionInProgress(item)"
                        v-tooltip.top="sendInvoiceTooltip(item)"
                        class="p-button-outlined p-button-secondary p-button-sm p-d-flex p-ai-center p-jc-center"
                        style="width: 36px;"
                        @click="handleSendInvoice(item)">
                  <i class="ti-email"/>
                </Button>
              </td>

              <td>
                <div v-if="item.sent_at" class="p-d-flex p-jc-center p-ai-center p-p-2">
                  <i style="font-size: 1.1em" class="ti-email p-text-secondary" v-tooltip.top="$t('statusSent') + ': ' + formatDate(item.sent_at)"/>
                </div>
              </td>

              <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.creator) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td v-show="actionButtonsCount">
                <div class="actions">
                  <!--                  <Button icon="ti-check"-->
                  <!--                          v-tooltip="$t('Confirm')"-->
                  <!--                          class="p-button-raised p-button-warning"-->
                  <!--                          style="margin: 0 3px;"-->
                  <!--                          @click="issueInvoiceOnClick(item)"-->
                  <!--                          :disabled="!showModals || dataTableIsLoading"/>-->
                  <!--                  <Button v-if="canIssueInvoice(item)"-->
                  <!--                          icon="ti-check"-->
                  <!--                          v-tooltip.top="$t('Confirm')"-->
                  <!--                          class="p-button-raised p-button-warning"-->
                  <!--                          style="margin: 0 3px;"-->
                  <!--                          @click="issueInvoiceOnClick(item)"-->
                  <!--                          :disabled="!showModals || dataTableIsLoading || invoiceActionInProgress(item)"/>-->
                  <BillingInvoiceActionsButton :invoice="item"
                                               :disabled="!showModals || dataTableIsLoading"
                                               :processing="invoiceActionInProgress(item)"
                                               @action="handleInvoiceAction"/>
                  <!--                  <EditButton :disabled="!showModals || dataTableIsLoading"-->
                  <!--                              @click="editItem(item)"/>-->
                  <EditButton :disabled="!showModals|| dataTableIsLoading|| [5, 6].includes(+item.state)" @click="editItem(item)"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <BillingInvoiceModal v-if="showModals"
                             :visible="itemModal"
                             :item="item"
                             @update-items="updateItems"
                             @close="closeItemModal"/>

        <ConfirmDeleteModal v-if="showModals"
                            :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            :data="itemToDeleteData"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"/>

        <ConfirmModal v-if="showModals"
                      :visible="invoiceConfirmModal"
                      :text="invoiceConfirmText"
                      :showSpinner="invoiceConfirmIsLoading"
                      @confirm="confirmInvoiceAction"
                      @close="closeInvoiceConfirmModal"/>

        <ConfirmModal v-if="showModals"
                      :visible="generateAllConfirmModal"
                      :showSpinner="generateAllIsLoading"
                      @confirm="generateAllBillingInvoices"
                      @close="closeGenerateAllConfirm"/>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import BillingInvoiceStates from '@/translations/states/BillingInvoiceStates'
import formatMixins from '@/mixins/formatMixins'
import calculateMixins from '@/mixins/calculateMixins'
import httpMixins from '@/mixins/httpMixins'
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import DatesRangeCalendar from "@/components/DatesRangeCalendar";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import DownloadPdfButton from "@/components/DownloadPdfButton.vue";
import templateLanguageMixin from '@/mixins/templateLanguageMixin'
import ChangedAfterExportIcon from "@/components/ChangedAfterExportIcon.vue";
import BillingInvoiceModal from "@/pages/system/components/BillingInvoiceModal";
import BillingInvoiceActionsButton from "@/pages/system/components/BillingInvoiceActionsButton.vue";

export default {
  mixins: [ formatMixins, calculateMixins, httpMixins, datatableMixins, showErrorsMixins, templateLanguageMixin ],
  components: {
    DownloadPdfButton,
    BillingInvoiceModal,
    DatesRangeCalendar,
    ChangedAfterExportIcon,
    BillingInvoiceActionsButton
  },
  data() {
    return {
      generateAllConfirmModal: false,
      generateAllIsLoading: false,
      invoiceConfirmModal: false,
      invoiceConfirmText: '',
      pendingInvoiceAction: null,
      showOnlyUnpaid: false,
      invoiceActionProcessingId: null,
      invoiceActionProcessingType: null,
      apiLink: 'billing-invoice-system',
      deleteItemUrl: 'billing-invoice-system/delete',
      routePath: '/billing-invoices',
      sort: {
        column: 'invoice_date',
        direction: '-',
      },
      BillingInvoiceStates,
    }
  },
  watch: {
    '$store.state.firstLayerIsOpened'(value) {
      if (!value) {
        if (this.invoiceConfirmModal) {
          this.closeInvoiceConfirmModal()
        }
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
  },
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return

      this.getItems()
    },

    openGenerateAllConfirm() {
      if (this.generateAllIsLoading || this.dataTableIsLoading) return

      this.generateAllConfirmModal = true
      this.$store.commit('toggleFirstLayer', true)
    },

    closeGenerateAllConfirm(force = false) {
      if (this.generateAllIsLoading && !force) return

      this.generateAllConfirmModal = false
      this.$store.commit('toggleFirstLayer', false)
    },

    async generateAllBillingInvoices() {
      if (this.generateAllIsLoading) return

      this.generateAllIsLoading = true
      this.dataTableIsLoading = true

      let completed = false

      try {
        const { data } = await httpClient.post(
            'billing-invoice-system/generate-all'
        )

        if (!data?.success) {
          this.showUnknownError()
          return
        }

        await this.getItems(true)

        const hasErrors = Number(data.failed || 0) > 0

        this.$toast.add({
          severity: hasErrors ? 'warn' : 'success',
          // summary: this.$t(hasErrors ? 'Warning' : 'Successful'),
          // detail: this.$t('Data saved', {
          //   created: data.created || 0,
          //   exists: data.already_exists || 0,
          //   skipped: data.skipped || 0,
          //   failed: data.failed || 0,
          // }),
          detail: this.$t('Data saved'),
          life: hasErrors
              ? settings.toastLife * 2
              : settings.toastLife,
        })

        completed = true

      } catch (err) {
        this.showError?.(err)

      } finally {
        this.generateAllIsLoading = false
        this.dataTableIsLoading = false

        if (completed) {
          this.closeGenerateAllConfirm(true)
        }
      }
    },

    handleInvoiceAction({ action, invoice }) {
      if (!invoice?.id) return

      if (action === 'delete') {
        this.confirmItemDelete(invoice)
        return
      }

      const handlers = {
        issue: this.issueInvoiceOnClick,
        mark_paid: this.markInvoiceAsPaidOnClick,
        void: this.voidInvoiceOnClick,
        cancel: this.cancelInvoiceOnClick,
        restore: this.restoreInvoiceOnClick,
        return_to_draft: this.returnInvoiceToDraftOnClick,
      }

      handlers[action]?.(invoice)
    },

    invoiceLabel(invoice) {
      return invoice?.number || `#${invoice?.id}`
    },

    openInvoiceConfirm(invoice, config) {
      if (
          !invoice?.id
          || this.invoiceActionInProgress(invoice)
          || this.invoiceConfirmModal
      ) {
        return
      }

      this.pendingInvoiceAction = {
        invoice,
        config,
      }

      this.invoiceConfirmText =
          config.confirmText || this.$t('confirm.proceed')

      this.invoiceConfirmModal = true
      this.$store.commit('toggleFirstLayer', true)
    },

    closeInvoiceConfirmModal() {
      if (this.invoiceConfirmIsLoading) {
        return
      }

      this.invoiceConfirmModal = false
      this.invoiceConfirmText = ''
      this.pendingInvoiceAction = null

      this.$store.commit('toggleFirstLayer', false)
    },

    async confirmInvoiceAction() {
      const pending = this.pendingInvoiceAction

      if (!pending?.invoice || !pending?.config) {
        return
      }

      const success = await this.runInvoiceAction(
          pending.invoice,
          pending.config
      )

      if (success) {
        this.closeInvoiceConfirmModal()
      }
    },

    async runInvoiceAction(invoice, config) {
      if (!invoice?.id || this.invoiceActionInProgress(invoice)) {
        return false
      }

      this.invoiceActionProcessingId = invoice.id
      this.invoiceActionProcessingType = config.type
      this.dataTableIsLoading = true

      try {
        const { data } = await httpClient.post(config.url, {
          id: invoice.id,
          ...(config.payload || {}),
        })

        if (!data?.success) {
          this.showUnknownError()
          return false
        }

        this.updateItems(invoice.id, false)
        await this.getItems()

        const successText =
            typeof config.successText === 'function'
                ? config.successText(data)
                : config.successText

        this.$toast.add({
          severity: 'success',
          summary: this.$t('Successful'),
          detail: successText || this.$t('Successful'),
          life: settings.toastLife,
        })

        return true

      } catch (err) {
        this.showError?.(err)
        return false

      } finally {
        this.invoiceActionProcessingId = null
        this.invoiceActionProcessingType = null
        this.dataTableIsLoading = false
      }
    },

    issueInvoiceOnClick(invoice) {
      const label = this.invoiceLabel(invoice)

      return this.openInvoiceConfirm(invoice, {
        type: 'issue',
        url: 'billing-invoice-system/issue',
        confirmText: this.$t('billing.invoices.issueConfirm', {
          invoice: label,
        }),
        successText: this.$t('billing.invoices.issueSuccess'),
      })
    },

    markInvoiceAsPaidOnClick(invoice) {
      const label = this.invoiceLabel(invoice)

      return this.openInvoiceConfirm(invoice, {
        type: 'paid',
        url: 'billing-invoice-system/mark-as-paid',
        confirmText: this.$t('billing.invoices.paymentConfirm', {
          invoice: label,
        }),
        successText: this.$t('billing.invoices.paymentSuccess'),
      })
    },

    voidInvoiceOnClick(invoice) {
      const label = this.invoiceLabel(invoice)

      return this.openInvoiceConfirm(invoice, {
        type: 'void',
        url: 'billing-invoice-system/void',
        confirmText: this.$t('billing.invoices.voidConfirm', {
          invoice: label,
        }),
        successText: this.$t('billing.invoices.voidSuccess'),
      })
    },

    cancelInvoiceOnClick(invoice) {
      const label = this.invoiceLabel(invoice)

      return this.openInvoiceConfirm(invoice, {
        type: 'cancel',
        url: 'billing-invoice-system/cancel',
        confirmText: this.$t('billing.invoices.cancelDraftConfirm', {
          invoice: label,
        }),
        successText: this.$t('billing.invoices.cancelDraftSuccess'),
      })
    },

    restoreInvoiceOnClick(invoice) {
      const label = this.invoiceLabel(invoice)
      const isCanceledDraft = +invoice.state === 5

      return this.openInvoiceConfirm(invoice, {
        type: 'restore',
        url: 'billing-invoice-system/restore',

        confirmText: isCanceledDraft
            ? this.$t('billing.invoices.restoreDraftConfirm', {
              invoice: label,
            })
            : this.$t('billing.invoices.restoreVoidConfirm', {
              invoice: label,
            }),

        successText: isCanceledDraft
            ? this.$t('billing.invoices.restoreDraftSuccess')
            : this.$t('billing.invoices.restoreSuccess'),
      })
    },

    returnInvoiceToDraftOnClick(invoice) {
      const label = this.invoiceLabel(invoice)

      return this.openInvoiceConfirm(invoice, {
        type: 'return_to_draft',
        url: 'billing-invoice-system/return-to-draft',
        confirmText: this.$t('billing.invoices.returnToDraftConfirm', {
          invoice: label,
        }),
        successText: this.$t('billing.invoices.returnToDraftSuccess'),
      })
    },

    canIssueInvoice(invoice) {
      return !!invoice?.id && +invoice.state === 1
    },

    canSendInvoice(invoice) {
      return !!invoice?.id &&
          [2, 3, 4, 7, 8].includes(+invoice.state) &&
          !!invoice.billing_email
    },

    invoiceActionInProgress(invoice, type = null) {
      if (!invoice?.id) return false

      if (+this.invoiceActionProcessingId !== +invoice.id) {
        return false
      }

      return type === null || this.invoiceActionProcessingType === type
    },

    sendInvoiceTooltip(invoice) {
      if (!invoice?.id) {
        return this.$t('Send')
      }

      if (+invoice.state === 1) {
        return this.$t('billing.invoices.issueBeforeSendTooltip')
      }

      if (![2, 3, 4, 7, 8].includes(+invoice.state)) {
        return this.$t('billing.invoices.cannotSendTooltip')
      }

      if (!invoice.billing_email) {
        return this.$t('billing.invoices.emailMissingTooltip')
      }

      if (invoice.sent_at) {
        return this.$t('billing.invoices.resendTooltip')
      }

      return this.$t('Send')
    },

    handleSendInvoice(invoice) {
      if (!this.canSendInvoice(invoice)) {
        return
      }

      const label = this.invoiceLabel(invoice)
      const force = !!invoice.sent_at
      const email = invoice.billing_email

      return this.openInvoiceConfirm(invoice, {
        type: 'send',
        url: 'billing-invoice-system/send-email',

        payload: {
          force: force ? 1 : 0,
        },

        confirmText: force
            ? this.$t('billing.invoices.resendConfirm', {
              invoice: label,
              email,
            })
            : this.$t('billing.invoices.sendConfirm', {
              invoice: label,
              email,
            }),

        successText: data => {
          const sentTo = data.to_email || email
          const isTestMode =
              data.email_mode && data.email_mode !== 'live'

          return isTestMode
              ? this.$t('billing.invoices.testEmailSentSuccess', {
                email: sentTo,
              })
              : this.$t('billing.invoices.emailSentSuccess', {
                email: sentTo,
              })
        },
      })
    },
    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      if (showSpinner || this.itemNotFound) this.isLoading = true

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController('systemBillingInvoices')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.isFocusMode ? null : (this.processedSearchValue ?? null),
            expand: this.fieldsExpand,
            show_only_unpaid: this.isFocusMode ? 0 : +this.showOnlyUnpaid,
          },
          data: {
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
          },
          signal: ctrl?.signal,
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }

          if (this.$route.query.show && (data.length === 0)) {
            this.showItemNotFoundError()
            this.itemNotFound = true
            return
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.showError?.(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.systemBillingInvoices === ctrl) {
          this._abortors.systemBillingInvoices = null
        }
      }
    },
    countOverdueDays(paymentDueDay) {
      if (!paymentDueDay) return null

      const now = new Date()
      const due = new Date(Number(paymentDueDay) * 1000)

      const todayUtc = Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate()
      )

      const dueUtc = Date.UTC(
          due.getUTCFullYear(),
          due.getUTCMonth(),
          due.getUTCDate()
      )

      return Math.round((dueUtc - todayUtc) / 86400000)
    },
  },
  computed: {
    invoiceConfirmIsLoading() {
      return (
          this.invoiceConfirmModal
          && this.invoiceActionProcessingId !== null
      )
    },
    actionButtonsCount() {
      // return this.canManage ? 2 : 0
      return 2
    },
    itemToDeleteData() {
      return  this.itemToDelete?.number ?? null
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}
</style>
